import {FieldDatePicker, FieldInput, Icon, PhoneNumberField} from '@sollapay/ui/components';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {PepSection, PepToggleField, usePepParties} from '@/components/pep';
import {ControlledAddressField} from '@/features/address';
import {AiFlashWrapper, useAiFillFlash, useFieldAiProps} from '@/features/ai-extraction';
import {TaxResidencyFields} from '@/features/buyers/components';
import {getNestedFieldError} from '@/features/buyers/utils';
import {
  generateDateOfBirth,
  generateIsraeliAddress,
  generatePhoneNumber,
  withDebugFieldChange,
  withDebugSetValue
} from '@/features/debug-test-data';
import {emailRule, futureDateRule, phoneRule} from '@/lib/validation';

import {BuyerIdentityFields} from './BuyerIdentityFields';

import type {AiSuggestion} from '@/features/ai-extraction';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {FC, ReactNode} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface BuyerPersonFieldsProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  index: number;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  markFieldModified: (field: string) => void;
  uploadStatus?: ReactNode;
}

export const BuyerPersonFields: FC<BuyerPersonFieldsProps> = ({
  form,
  index,
  aiFields,
  isProcessing,
  markFieldModified,
  uploadStatus
}) => {
  const {t} = useTranslation('trustAccounts');
  const {
    register,
    control,
    formState: {errors}
  } = form;
  const prefix = 'view.buyers.addBuyerModal.buyerDetails';
  const setDebugValue = form.setValue as DebugSetValue;

  const fp = (name: string) => `buyers.${index}.person.${name}`;
  const fieldAi = useFieldAiProps({
    aiFields,
    isProcessing,
    markFieldModified,
    getValue: form.getValues
  });
  const isFlashing = useAiFillFlash(aiFields);

  const buyerErrors = errors.buyers?.[index]?.person;
  const pepParties = usePepParties({
    control,
    isCorporate: false,
    pepPath: `buyers.${index}.person.pep`
  });

  return (
    <>
      <BuyerIdentityFields
        form={form}
        index={index}
        fieldAi={fieldAi}
        isFlashing={isFlashing}
        uploadStatus={uploadStatus}
      />

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper field={fp('email')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <FieldInput
              label={t(`${prefix}.email`)}
              placeholder={t(`${prefix}.emailPlaceholder`)}
              error={buyerErrors?.email?.message}
              type="email"
              required
              startAdornment={<Icon name="mail" className="size-4" />}
              cornerBadge={ai.aiIndicator}
              isLoading={ai.aiGenerating}
              {...register(`buyers.${index}.person.email` as `buyers.0.person.email`, {
                ...emailRule(t),
                onChange: () => ai.onAiChange()
              })}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper field={fp('phone')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <Controller
              name={`buyers.${index}.person.phone` as `buyers.0.person.phone`}
              control={control}
              rules={phoneRule(t)}
              render={({field}) => (
                <PhoneNumberField
                  label={t(`${prefix}.phone`)}
                  placeholder={t(`${prefix}.phonePlaceholder`)}
                  error={buyerErrors?.phone?.message}
                  required
                  cornerBadge={withDebugFieldChange(
                    ai.aiIndicator,
                    field.onChange,
                    generatePhoneNumber,
                    ai.onAiChange
                  )}
                  isLoading={ai.aiGenerating}
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    ai.onAiChange();
                  }}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
            />
          )}
        </AiFlashWrapper>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper field={fp('dateOfBirth')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <Controller
              name={`buyers.${index}.person.dateOfBirth` as `buyers.0.person.dateOfBirth`}
              control={control}
              rules={{required: t('common:fields.dateOfBirth.required'), ...futureDateRule(t)}}
              render={({field}) => (
                <FieldDatePicker
                  label={t(`${prefix}.dateOfBirth`)}
                  placeholder={t(`${prefix}.dateOfBirthPlaceholder`)}
                  error={buyerErrors?.dateOfBirth?.message}
                  required
                  disableFuture
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    ai.onAiChange();
                  }}
                  onBlur={field.onBlur}
                  cornerBadge={withDebugFieldChange(
                    ai.aiIndicator,
                    field.onChange,
                    generateDateOfBirth,
                    ai.onAiChange
                  )}
                  isLoading={ai.aiGenerating}
                />
              )}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper field={fp('address')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <ControlledAddressField
              control={control}
              name={`buyers.${index}.person.address` as `buyers.0.person.address`}
              label={t(`${prefix}.address`)}
              error={buyerErrors?.address?.message}
              cornerBadge={withDebugSetValue(
                ai.aiIndicator,
                setDebugValue,
                fp('address'),
                generateIsraeliAddress,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              onAiChange={ai.onAiChange}
            />
          )}
        </AiFlashWrapper>
      </div>

      <TaxResidencyFields
        control={control}
        taxResidencyName={`buyers.${index}.person.taxResidency` as `buyers.0.person.taxResidency`}
        taxResidencyCountryName={
          `buyers.${index}.person.taxResidencyCountry` as `buyers.0.person.taxResidencyCountry`
        }
        taxResidencyError={buyerErrors?.taxResidency?.message}
        taxResidencyCountryError={buyerErrors?.taxResidencyCountry?.message}
        i18nPrefix={prefix}
        extra={
          <PepToggleField
            control={form.control}
            isCorporate={false}
            pepPath={`buyers.${index}.person.pep`}
            toggleQuestionKey="toggleQuestionBuyer"
            replace={pepParties.replace}
          />
        }
      />

      <PepSection
        control={form.control}
        register={form.register}
        isCorporate={false}
        pepPath={`buyers.${index}.person.pep`}
        fe={(key: string) => getNestedFieldError(form.formState.errors, key)}
        fields={pepParties.fields}
        append={pepParties.append}
        remove={pepParties.remove}
      />
    </>
  );
};
